import { ActiveSearchTags } from '@/components/ActiveSearchTags';
import { SearchValueShow } from '@/components/ActiveSearchTags/types';
import { ReceivedInvoicesSearchQuery } from '@/context/services/invoice/type';
import { useRenewalUiReceivedInvoicesFlag } from '@/hooks/feature_flag/useRenewalUiReceivedInvoicesFlag';
import { useTranslation } from '@/i18n';
import { ReactNode, useMemo } from 'react';
import {
  APPLIED_SEARCH_TAG_MAX_WIDTH_PX,
  MAX_VISIBLE_APPLIED_SEARCH_TAGS,
  buildReceivedInvoiceAppliedSearchTags,
} from './helper';
import { useResolvedAppliedSearchNames } from './useResolvedAppliedSearchNames';

type AppliedSearchTagsProps = {
  searchQuery: ReceivedInvoicesSearchQuery;
};

const tagMaxWidth = (visibleTagCount: number): string | number | undefined => {
  if (visibleTagCount === 1) return '100%';
  if (visibleTagCount === MAX_VISIBLE_APPLIED_SEARCH_TAGS) {
    return APPLIED_SEARCH_TAG_MAX_WIDTH_PX;
  }
  return undefined;
};

const AppliedSearchTagsContent = ({ searchQuery }: AppliedSearchTagsProps) => {
  const { t } = useTranslation();
  const { payeeNames, picNames } = useResolvedAppliedSearchNames(searchQuery);
  const tags = useMemo(
    () =>
      buildReceivedInvoiceAppliedSearchTags({
        searchQuery,
        payeeNames,
        picNames,
        labels: {
          keyword: t('received_invoice_key_word'),
          payee: t('label_ap_payee'),
          amount: t('received_invoice_total_amount'),
          pic: t('received_invoice_pics'),
          dueDate: t('received_invoice_due_date'),
          uploadedAt: t('label_uploaded_at'),
          uploadType: t('label_upload_type'),
          operationStatus: t('label_operation_status'),
          reconciliation: t('received_invoice_reconciliation'),
          reconciliationStatus: {
            unset: t('received_invoice_reconciliation_status_unset'),
            excluded: t('received_invoice_reconciliation_status_excluded'),
            waiting: t('received_invoice_reconciliation_status_waiting'),
            sent: t('received_invoice_reconciliation_status_sent'),
          },
        },
      }),
    [payeeNames, picNames, searchQuery, t]
  );
  const activeSearchValues = useMemo<SearchValueShow[]>(
    () =>
      tags.map((tag) => ({
        fieldLabel: tag.fieldLabel,
        valueShow: tag.value,
        expandFully: tag.expandFully,
      })),
    [tags]
  );
  const options = useMemo(
    () => ({
      maxVisible: MAX_VISIBLE_APPLIED_SEARCH_TAGS,
      heading: t('received_invoice_applied_search_conditions'),
      overflowLabel: (count: number) =>
        t('received_invoice_applied_search_more', { count }),
      tagMaxWidth,
    }),
    [t]
  );

  return (
    <ActiveSearchTags
      activeSearchValues={activeSearchValues}
      options={options}
    />
  );
};

export const AppliedSearchTags = ({
  searchQuery,
}: AppliedSearchTagsProps): ReactNode => {
  const isRenewalUiReceivedInvoices = useRenewalUiReceivedInvoicesFlag();
  if (!isRenewalUiReceivedInvoices) return null;
  return <AppliedSearchTagsContent searchQuery={searchQuery} />;
};
